import React, { useState } from 'react';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import { EmptyState } from '@/components/ui/hayat';
import axios from 'axios';

/**
 * Phase 16: Family Invite Page
 * Admin can invite new family members
 */
function FamilyInvitePage() {
  const { familyUser, getAuthHeader, isAdmin } = useFamilyAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invitationUrl, setInvitationUrl] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'member'
  });
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setInvitationUrl('');
    
    if (!formData.email || !formData.name) {
      setError('Bitte füllen Sie alle Felder aus.');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/family/invite`,
        formData,
        { headers: getAuthHeader() }
      );
      
      const token = response.data.invitation_token;
      const url = `${window.location.origin}/family/invite/accept/${token}`;
      
      setSuccess(`Einladung erfolgreich erstellt! Senden Sie den Link an ${formData.name}.`);
      setInvitationUrl(url);
      
      // Reset form
      setFormData({ email: '', name: '', role: 'member' });
    } catch (err) {
      console.error('Error creating invitation:', err);
      setError(err.response?.data?.detail || 'Fehler beim Erstellen der Einladung.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#050816] pb-24 page-transition">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <EmptyState
            icon="🔒"
            title="Zugriff verweigert"
            description="Nur Admins können Familienmitglieder einladen."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] pb-24 page-transition">
      {/* Header */}
      <div className="bg-neutral-900/60 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40 glass-effect">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-[#F9FAFB] animate-fade-in-down">
            Familienmitglied einladen 👥
          </h1>
          <p className="text-neutral-500 font-medium mt-1">
            Laden Sie neue Mitglieder zu Ihrem Haushalt ein
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {success && (
          <div className="bg-hayat-success-50 border border-hayat-success-200 text-hayat-success-700 px-6 py-4 rounded-2xl mb-6 animate-fade-in">
            <p className="font-semibold mb-2">✓ {success}</p>
            {invitationUrl && (
              <div className="mt-4">
                <p className="text-sm font-semibold mb-2">Einladungslink:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={invitationUrl}
                    readOnly
                    className="flex-1 px-4 py-2 bg-white border border-hayat-success-300 rounded-xl text-sm font-mono"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(invitationUrl);
                      alert('Link kopiert!');
                    }}
                    className="px-4 py-2 bg-hayat-success-500 text-white rounded-xl hover:bg-hayat-success-600 transition-all font-semibold"
                  >
                    Kopieren
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="bg-hayat-danger-50 border border-hayat-danger-200 text-hayat-danger-700 px-6 py-4 rounded-2xl mb-6 animate-fade-in">
            <p className="font-semibold">⚠️ {error}</p>
          </div>
        )}

        {/* Form */}
        <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md border border-neutral-600/40">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Maria Mustermann"
                className="w-full px-4 py-3 border-2 border-hayat-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-hayat-primary-500 focus:border-transparent transition-all"
                disabled={loading}
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
                E-Mail-Adresse *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="maria@example.com"
                className="w-full px-4 py-3 border-2 border-hayat-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-hayat-primary-500 focus:border-transparent transition-all"
                disabled={loading}
                required
              />
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
                Rolle
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'member' })}
                  className={`px-6 py-4 rounded-2xl font-semibold transition-all duration-300 ${
                    formData.role === 'member'
                      ? 'bg-[#5B4B8A] text-white shadow-hayat-md scale-105'
                      : 'bg-neutral-800/50 text-[#CBD5F5] hover:bg-hayat-neutral-100 border border-hayat-neutral-200'
                  }`}
                  disabled={loading}
                >
                  <div className="text-2xl mb-1">👤</div>
                  <div className="text-sm">Mitglied</div>
                  <div className="text-xs opacity-75">Nur lesen</div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'admin' })}
                  className={`px-6 py-4 rounded-2xl font-semibold transition-all duration-300 ${
                    formData.role === 'admin'
                      ? 'bg-[#5B4B8A] text-white shadow-hayat-md scale-105'
                      : 'bg-neutral-800/50 text-[#CBD5F5] hover:bg-hayat-neutral-100 border border-hayat-neutral-200'
                  }`}
                  disabled={loading}
                >
                  <div className="text-2xl mb-1">🔑</div>
                  <div className="text-sm">Admin</div>
                  <div className="text-xs opacity-75">Volle Rechte</div>
                </button>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-hayat-secondary-50 border border-hayat-secondary-200 px-4 py-3 rounded-2xl text-sm text-hayat-secondary-800">
              <p className="font-semibold mb-1">ℹ️ Hinweis</p>
              <p>Die eingeladene Person erhält einen Link per E-Mail. Der Link ist 48 Stunden gültig.</p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="w-full py-4 px-6 bg-gradient-to-r from-[#5B4B8A] to-[#4a3d72] text-white rounded-2xl hover:shadow-hayat-lg transition-all font-bold hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Wird erstellt...
                </span>
              ) : (
                'Einladung erstellen'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default FamilyInvitePage;
